<template>
  <div
    @touchstart="isMobile ? DragEnable($event) : ''"
    @touchend="isMobile ? setEnableDragFalse() : ''"
    @mousedown="!isMobile ? DragEnable($event) : ''"
    @mouseup="!isMobile ? setEnableDragFalse() : ''"
    class="drag_wrapper"
    ref="$drag"
    :class="[!dragModel.AllowDrag ? disabledClass : '', dragModel.IsDragging ? dragClass : '']"
    tabindex="0"
    @click="onWrapperClick"
  >
    <slot />
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted, PropType, ref, useSlots, watch } from "vue";
import DragModel from "./models/DragModel";
import { useRootFontSize } from "tir-pm-toolkit";

const props = defineProps({
  /**Выкл/вкл пертаскивания */
  enabled: { default: true },
  /**Модель для управления компонентом */
  dragModel: { default: null, type: Object as PropType<DragModel<Object>> },
  /**Класс для перетаскиваемого клона элемента помещаемого в slot */
  dragClass: { default: "dragging", type: String },
  /**Класс для отображения элемента при состоянии dragModel.AllowDrop == true */
  disabledClass: { default: "disabled", type: String },
  /**Функция для проверки разрешения дропа */
  allowDropFunc: {
    type: Function,
    default: () => true,
  },
  /**Позволить скроллить во время драга */
  isAllowScroll: { default: false, type: Boolean },
  /**Скорость скролла при касании */
  dragScrollSpeed: { default: 10, type: Number },
  /**Включение задержки перед драгом */
  isAllowClickDelay: { default: false, type: Boolean },
  /**Задержка для считывания клика */
  clickDelay: { default: 300, type: Number },
  /**Включен ли расчет координат по центру перетаскиваемого блока */
  isEnableBlockMiddleCoordsSending: { default: false, type: Boolean },
  /**Координаты перетаскиваемого блока */
  cursor: { default: null, type: Object as PropType<{ x: number; y: number }> },
  /**Область прокрутки */
  scrollArea: { default: null, type: HTMLElement },
  /**Активна ли анимация для дропа */
  isDropAnimation: { default: false, type: Boolean },
});
/**События компонента */
const emit = defineEmits(["take", "cancelTake", "cursor", "dragging", "over", "drop"]);
/**Слоты компонента */
const slots = useSlots();

const { rootFontSizePx } = useRootFontSize();

/**X координата курсора */
const x = ref<number>(null);
/**Y координата курсора */
const y = ref<number>(null);
/**Находится ли курсор над элементом */
const isOver = ref<boolean>(false);
/**Координаты элемента в slot */
const coords = ref<{ top: number; left: number }>();
/**Клон slot элемента */
const cloneElement = ref<HTMLElement>();
/**Ширина элемента */
const elWidth = ref<string>("");
const shiftX = ref<number>(null);
const shiftY = ref<number>(null);
const scrollInterval = ref<any>(null);
const tempElTop = ref<number>(null);
const isDragBegin = ref<boolean>(true);
const enableDrag = ref<boolean>(true);
const delayFirstMouseEvent = ref<MouseEvent | TouchEvent | Touch>(null);
const delayMouseEvent = ref<MouseEvent | TouchEvent | Touch>(null);
const wrapperScrollY = ref<number>(0);
const isMobile = ref<boolean>(false);
const clickDelayTimeout = ref<any>(null);

const $drag = ref<HTMLElement>(null as any);

const dragModel = computed<DragModel<any>>(() => props.dragModel);

watch(dragModel, onUpdateDragModel, { deep: true });

function onUpdateDragModel() {
  if (!dragModel.value || (!dragModel.value.AllowDrag && !dragModel.value.AllowDrop))
    removeEventListeners();
  else addEventListeners();
}

const elTop = computed<number>(() => {
  if (cloneElement.value) {
    return (
      document.documentElement.clientHeight -
      cloneElement.value?.getBoundingClientRect().y -
      cloneElement.value?.getBoundingClientRect().height
    );
  }
});

//После монтирования создается слушатель на движение курсора
onMounted(() => {
  document.addEventListener("touchmove", disableTouchScroll, {
    passive: false,
  } as EventListenerOptions);
  checkIsMobile(navigator.userAgent || navigator.vendor || (window as any).opera);
  onUpdateDragModel();
});

function addEventListeners() {
  document.addEventListener("click", onClickDocument);

  if (isMobile.value) {
    document.addEventListener("touchmove", onTouchMove);
    document.addEventListener("touchend", targetOnMouseUp);
    document.addEventListener("touchstart", targetOnTouchStart, { passive: false });
  } else {
    document.addEventListener("mousemove", targetOnMouseMove);
    document.addEventListener("mouseup", targetOnMouseUp);
  }
}
function removeEventListeners() {
  document.removeEventListener("click", onClickDocument);

  if (isMobile.value) {
    document.removeEventListener("touchmove", onTouchMove);
    document.removeEventListener("touchend", targetOnMouseUp);
    document.removeEventListener("touchstart", targetOnTouchStart);
    document.removeEventListener("touchmove", disableTouchScroll, {
      passive: false,
    } as EventListenerOptions);
  } else {
    document.removeEventListener("mousemove", targetOnMouseMove);
    document.removeEventListener("mouseup", targetOnMouseUp);
  }
}
function targetOnTouchStart(e: TouchEvent) {
  targetOnMouseMove(e.touches[0]);
}

const DragEnable = (event: any) => {
  if (cloneElement.value) {
    document.body.removeChild(cloneElement.value);
    cloneElement.value = null;
  }
  if (!dragModel.value.AllowDrag) return;
  enableDrag.value = true;

  if (props.isAllowClickDelay) {
    document.addEventListener("mouseup", onDelayMouseUp);
    document.addEventListener("mousemove", onDelayMouseMove);

    if (isMobile.value && event.touches && event.touches[0]) event = event.touches[0];

    delayFirstMouseEvent.value = event;
    delayMouseEvent.value = event;

    if (enableDrag.value) {
      clickDelayTimeout.value = setTimeout(() => {
        startDrag(delayMouseEvent.value);
        document.removeEventListener("mouseup", onDelayMouseUp);
        document.removeEventListener("mousemove", onDelayMouseMove);
        delayMouseEvent.value = null;
        delayFirstMouseEvent.value = null;
      }, props.clickDelay);
    }
  } else {
    startDrag(event);
    if (isMobile.value) targetOnMouseMove((event as TouchEvent).touches[0]);
  }
};

function onDelayMouseUp() {
  enableDrag.value = false;
  clearTimeout(clickDelayTimeout.value);
  document.removeEventListener("mouseup", onDelayMouseUp);
}

function onDelayMouseMove(event: MouseEvent) {
  delayMouseEvent.value = event;
}

function startDrag(event: MouseEvent | TouchEvent | Touch) {
  if (enableDrag.value) {
    onMouseDown(event);
    clearSelection();
    enableDrag.value = false;
  }
}

function clearSelection() {
  if (document.getSelection() && document.getSelection().empty) {
    document.getSelection().empty();
  } else if (window.getSelection) {
    const sel = window.getSelection();
    sel.removeAllRanges();
  }
}

//При взятии
function doTake() {
  emit("take", true, dragModel);
}
//При потере фокуса
function doCancelTake() {
  if (!props.dragModel.IsDragging) return;
  props.dragModel.IsDragging = false;
  emit("cancelTake", props.dragModel);
}
//При клике по обертке
const onWrapperClick = () => {
  if (dragModel.value.IsDragging || !dragModel.value.AllowDrag) return;
  doTake();
  document.addEventListener("click", onClickDocument);
};

//Получение цели дропа
function getDragComponent(clickElement: Node): any {
  let targetElement = clickElement;
  do {
    targetElement = targetElement.parentNode;
  } while (targetElement);
  return undefined;
}
//При клике по документу
function onClickDocument(event: MouseEvent | TouchEvent): void {
  const getClickTarget = getDragComponent(event.target as Node);
  if (getClickTarget == $drag.value) {
    doCancelTake();
    return;
  }
  if (!getClickTarget || !getClickTarget.isAllowDrop($drag.value)) emit("cancelTake", dragModel);
}

//Функция для разрешения дропа
function isAllowDrop(dragElement: any): boolean {
  return dragModel.value.AllowDrop && props.allowDropFunc(dragElement.dragModel, dragModel);
}

//При нажатии на drag-source
function onMouseDown(event: any): void {
  if (!props.enabled || !dragModel.value.AllowDrag) return;

  if (!props.isAllowScroll) scrollDisable(true);

  let slot: string = $drag.value.innerHTML;
  let elm: any = document.createElement("div");
  elm.innerHTML = slot;
  elm = elm.firstChild;

  if ($drag.value) {
    elWidth.value = $drag.value?.getBoundingClientRect()?.width / rootFontSizePx.value + "rem";
  }

  disableUserSelect();
  if ((event as TouchEvent).touches && (event as TouchEvent).touches.length) {
    event = (event as TouchEvent).touches[0];
  }

  !isMobile.value && event.preventDefault();

  setCoords();

  cloneElement.value = elm.cloneNode(true) as HTMLElement;
  cloneElement.value.focus();

  if (!props.isAllowClickDelay) {
    shiftX.value = (event as MouseEvent | Touch).pageX - coords.value.left;
    shiftY.value = (event as MouseEvent | Touch).pageY - coords.value.top;
  } else {
    shiftX.value = (delayFirstMouseEvent.value as MouseEvent | Touch).pageX - coords.value.left;
    shiftY.value = (delayFirstMouseEvent.value as MouseEvent | Touch).pageY - coords.value.top;
  }
  cloneElement.value.style.position = "absolute";
  cloneElement.value.classList.add(props.dragClass);
  document.body.appendChild(cloneElement.value);
  cloneElement.value.style.width = elWidth.value;
  if (!props.isAllowClickDelay) moveAt(event as MouseEvent | Touch, cloneElement.value);
  else moveAt(delayMouseEvent.value as MouseEvent | Touch, cloneElement.value);
  addMoveAndUpMouseEvents();
}

function scrollDisable(value: boolean) {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|mobi/i.test(navigator.userAgent)
  ) {
    if (value) {
      document.body.style.touchAction = "none";
    }
  }
  if (!value) {
    document.body.style.touchAction = "";
  }
}

function disableUserSelect() {
  let body: any = document.querySelector("body");
  body.style.webkitUserSelect = "none";
  body.style.khtmlUserSelect = "none";
  body.style.MozUserSelect = "none";
  body.style.msUserSelect = "none";
  body.style.OUserSelect = "none";
  body.style.userSelect = "none";
}

function enableUserSelect() {
  let body: any = document.querySelector("body");
  body.style.webkitUserSelect = "";
  body.style.khtmlUserSelect = "";
  body.style.MozUserSelect = "";
  body.style.msUserSelect = "";
  body.style.OUserSelect = "";
  body.style.userSelect = "";
}

function checkIsMobile(a: any) {
  if (
    /(android|ipad|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      a,
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      a.substr(0, 4),
    ) ||
    /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
      a.toLowerCase(),
    ) ||
    "ontouchstart" in window
  )
    isMobile.value = true;
}

const setEnableDragFalse = () => {
  enableDrag.value = false;
};

//Добавление событий
function addMoveAndUpMouseEvents() {
  if (!props.isAllowScroll) {
    document.addEventListener("wheel", preventScroll, {
      passive: false,
    } as EventListenerOptions);
  } else {
    document.addEventListener("scroll", onScroll);
  }
  if (isMobile.value) {
    document.addEventListener("touchmove", onMouseMove);
    document.addEventListener("touchend", onMouseUp);
  } else {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }
}

function onScroll(e: Event) {
  moveAt(null, cloneElement.value, undefined, true);
  if (!props.isAllowScroll) {
    e.preventDefault();
  }
  //по позиции скролла
}

//При движении мыши
function onMouseMove(event: any) {
  moveAt(event, cloneElement.value);

  if (props.isEnableBlockMiddleCoordsSending) {
    let rect = cloneElement.value?.getBoundingClientRect();
    let x = rect.x + rect.width / 2 + document.documentElement.scrollLeft;
    let y = rect.y + rect.height / 2 + document.documentElement.scrollTop;
    emit("cursor", { x: x, y: y });
  }
}
//Отключение скролла при перетягивании на телефоне
function disableTouchScroll(e: TouchEvent) {
  if (!cloneElement.value) return;

  moveAt(e.touches[0], cloneElement.value);
  if (e.cancelable) e.preventDefault();
}

//Установление координат элементы
function setCoords() {
  let box = $drag.value?.getBoundingClientRect();
  coords.value = {
    top: box.y + pageYOffset,
    left: box.x + pageXOffset,
  };
}

//При отпускании курсора
function onMouseUp() {
  if (cloneElement.value) {
    if (props.isDropAnimation) cloneElement.value.classList.add("drop-animation");
    setTimeout(
      () => {
        document.body.removeChild(cloneElement.value);
        cloneElement.value = null;
      },
      props.isDropAnimation ? 500 : 0,
    );
  }
  removeMoveAndUpMouseEvents();
  scrollDisable(false);
  emit("dragging", false, props.dragModel, false);
  clearInterval(scrollInterval.value);
  scrollInterval.value = null;
  enableUserSelect();
}

//Удаление событий
function removeMoveAndUpMouseEvents() {
  document.removeEventListener("wheel", preventScroll, {
    passive: false,
  } as EventListenerOptions);

  if (isMobile.value) {
    document.removeEventListener("touchmove", onMouseMove);
    document.removeEventListener("touchend", onMouseUp);
  } else {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }

  document.removeEventListener("scroll", onScroll);
}
//Предотвращение скролла при драге
function preventScroll(event: Event) {
  event.preventDefault();
}

//Перемещение блока вслед за курсором
function moveAt(
  event: MouseEvent | Touch,
  element: HTMLElement,
  isTop?: boolean,
  isScroll?: boolean,
) {
  let scrollLeft: number = document.documentElement.scrollLeft;
  let scrollTop: number = document.documentElement.scrollTop;
  let clientWidth: number = document.documentElement.clientWidth;
  let clientHeight: number = document.documentElement.clientHeight;

  if (isScroll) {
    if (element && !tempElTop.value) {
      let top = element?.getBoundingClientRect().y;
      if (top >= 0) tempElTop.value = top;
    }
    element.style.top = window.scrollY / rootFontSizePx.value + tempElTop.value / rootFontSizePx.value + "rem";
  }
  if (isTop !== undefined) {
    if (isTop) {
      element.style.top = window.scrollY / rootFontSizePx.value - props.dragScrollSpeed + 1 + "rem";
    } else {
      element.style.top =
        window.scrollY / rootFontSizePx.value +
        document.documentElement.clientHeight / rootFontSizePx.value -
        element.offsetHeight / rootFontSizePx.value +
        props.dragScrollSpeed -
        1 +
        "rem";
    }
  }
  if (isTop !== undefined || isScroll) {
    return;
  }
  emit("dragging", true, dragModel.value, isDragBegin.value);
  isDragBegin.value = false;
  tempElTop.value = null;
  const isOutLeftEdge = event.pageX - shiftX.value - scrollLeft <= 0;
  const isOutRightEdge =
    event.pageX - shiftX.value + cloneElement.value.offsetWidth - scrollLeft > clientWidth;
  const isOutTopEdge = event.pageY - shiftY.value - scrollTop <= 0;
  const isOutBottomEdge =
    event.pageY - shiftY.value + cloneElement.value.offsetHeight - scrollTop > clientHeight;
  if (!isOutLeftEdge && !isOutRightEdge) {
    element.style.left = event.pageX / rootFontSizePx.value - shiftX.value / rootFontSizePx.value + "rem";
  }
  if (!isOutTopEdge && !isOutBottomEdge) {
    element.style.top = event.pageY / rootFontSizePx.value - shiftY.value / rootFontSizePx.value + "rem";
    clearInterval(scrollInterval.value);
    scrollInterval.value = null;
  }
  if (isOutLeftEdge) {
    element.style.left = scrollLeft - 1 + "rem";
  }
  if (isOutRightEdge) {
    element.style.left = scrollLeft + clientWidth / rootFontSizePx.value - cloneElement.value.offsetWidth / rootFontSizePx.value - 1 + "rem";
  }

  let scrollArea: any = props.scrollArea;
  if (scrollArea) {
    scrollArea = scrollArea;
    wrapperScrollY.value = scrollArea.scrollTop;
  } else {
    scrollArea = window;
    wrapperScrollY.value = window.scrollY;
  }
  if (isOutTopEdge) {
    if (!scrollInterval.value && props.isAllowScroll) {
      scrollInterval.value = setInterval(() => {
        if (wrapperScrollY.value > 0 + props.dragScrollSpeed) {
          moveAt(null, cloneElement.value, true);
          scrollArea.scroll(0, wrapperScrollY.value - props.dragScrollSpeed);
          if (scrollArea) wrapperScrollY.value -= props.dragScrollSpeed;
        } else {
          clearInterval(scrollInterval.value);
          scrollInterval.value = null;
        }
      }, 10);
    }
    element.style.top = scrollTop + 1 + "rem";
  }
  if (isOutBottomEdge) {
    if (!scrollInterval.value && props.isAllowScroll) {
      scrollInterval.value = setInterval(() => {
        if (
          wrapperScrollY.value + document.documentElement.clientHeight <
            document.documentElement.scrollHeight - props.dragScrollSpeed ||
          (scrollArea &&
            wrapperScrollY.value + document.documentElement.clientHeight <
              scrollArea.scrollHeight - props.dragScrollSpeed)
        ) {
          moveAt(null, cloneElement.value, false);
          scrollArea.scroll(0, wrapperScrollY.value + props.dragScrollSpeed);
          if (scrollArea) wrapperScrollY.value += props.dragScrollSpeed;
        } else {
          clearInterval(scrollInterval.value);
          scrollInterval.value = null;
        }
      }, 10);
    }
    element.style.top = scrollTop + clientHeight / rootFontSizePx.value - cloneElement.value.offsetHeight / rootFontSizePx.value - 1 + "rem";
  }
}

//Если курсор находиться над target'ом метод отдаст emit
function changeIsOver(value: boolean) {
  if (isOver.value == value) return;

  isOver.value = value;
  if (value) emit("over", value, dragModel.value);
  else emit("over", value, null);
}

//При отпускании курсора
function targetOnMouseUp() {
  isDragBegin.value = true;
  if (isOver.value) {
    isOver.value = false;
    emit("drop", dragModel.value);
  }
}

//При движении курсора
function targetOnMouseMove(event: MouseEvent | Touch) {
  let self: HTMLElement = $drag.value as HTMLElement;
  if (event.pageX && event.pageY) {
    x.value = event.pageX;
    y.value = event.pageY;
  } else if (event.clientX && event.clientY) {
    x.value = event.clientX + self.scrollLeft - document.documentElement.clientLeft;
    y.value = event.clientY + self.scrollTop - document.documentElement.clientTop;
  } else {
    x.value = 0;
    y.value = 0;
  }
  checkMouseOver();
}

//Проверка находится ли курсор над drag-target
function checkMouseOver() {
  if (!$drag.value) return;
  let rect = ($drag.value as HTMLElement)?.getBoundingClientRect();

  let coordX = props.cursor ? props.cursor.x : x.value;
  let coordY = props.cursor ? props.cursor.y : y.value;

  const cursorIsOverComponent =
    coordX >= rect.x + document.documentElement.scrollLeft &&
    coordX <= rect.x + rect.width + document.documentElement.scrollLeft &&
    coordY >= rect.y + document.documentElement.scrollTop &&
    coordY <= rect.y + rect.height + document.documentElement.scrollTop;

  changeIsOver(cursorIsOverComponent);
}

//При движении курсора на телефоне
function onTouchMove(e: TouchEvent) {
  targetOnMouseMove(e.touches[0]);
}

//Перед уничтожением удаляется слушатель на движение курсора
onUnmounted(() => {
  removeEventListeners();
});
</script>
<style lang="scss">
.drag_wrapper {
  outline: none;
  user-select: none;
}

@keyframes drop {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(0.4);
    opacity: 0;
  }
}

.drop-animation {
  animation-duration: 500ms;
  animation-name: drop;
  opacity: 0;
}
</style>
